import React, { useState } from 'react';
import { FaQuestionCircle, FaTimes } from 'react-icons/fa';

const templateHelp = {
  'API': {
    title: "Comment utiliser un modèle d'API",
    description: "Les APIs sont des points d'accès qui exposent des fonctionnalités à d'autres services.",
    steps: [
      "Modifiez le nom pour refléter la fonction spécifique de votre API",
      "Ajustez les endpoints dans les métadonnées selon les ressources exposées",
      "Décrivez les formats de requête et réponse dans le schéma d'endpoint",
      "Liez cette API aux features qu'elle implémente ou aux services qu'elle utilise"
    ],
    example: "Pour une API de gestion utilisateurs, nommez-la 'UserAPI' et définissez des endpoints comme '/users', '/users/{id}', etc."
  },
  'Feature': {
    title: "Comment utiliser un modèle de Feature",
    description: "Les Features représentent des fonctionnalités visibles par l'utilisateur ou qui apportent une valeur métier.",
    steps: [
      "Donnez un nom descriptif qui reflète la fonctionnalité du point de vue utilisateur",
      "Définissez des critères d'acceptation clairs dans les métadonnées",
      "Identifiez les stakeholders concernés par cette fonctionnalité",
      "Liez cette feature aux APIs ou services qui l'implémentent"
    ],
    example: "Pour une fonctionnalité d'authentification, nommez-la 'UserAuthentication' et définissez des critères comme 'L'utilisateur peut se connecter avec email/mot de passe'."
  },
  'Service': {
    title: "Comment utiliser un modèle de Service",
    description: "Les Services encapsulent la logique métier et orchestrent différentes opérations.",
    steps: [
      "Choisissez un nom qui reflète la responsabilité principale du service",
      "Listez les dépendances dans les métadonnées",
      "Décrivez les principales fonctions ou méthodes fournies",
      "Connectez ce service aux bases de données qu'il utilise et aux APIs qu'il expose"
    ],
    example: "Pour un service de notification, nommez-le 'NotificationService' et décrivez sa capacité à envoyer des emails, SMS, etc."
  },
  'Database': {
    title: "Comment utiliser un modèle de Base de données",
    description: "Les nœuds Database représentent les solutions de stockage persistant de votre système.",
    steps: [
      "Nommez la base de données selon le domaine de données qu'elle stocke",
      "Décrivez les principales tables/collections dans les métadonnées",
      "Spécifiez la technologie utilisée (SQL, NoSQL, etc.)",
      "Connectez cette base aux services qui l'utilisent"
    ],
    example: "Pour une base de données de produits, nommez-la 'ProductDB' et listez des tables comme 'products', 'categories', etc."
  },
  'UI': {
    title: "Comment utiliser un modèle d'UI",
    description: "Les composants UI représentent les éléments d'interface utilisateur de votre application.",
    steps: [
      "Donnez un nom descriptif qui reflète le rôle du composant",
      "Décrivez les props et états dans les métadonnées",
      "Spécifiez la technologie frontend utilisée",
      "Connectez ce composant aux features qu'il implémente"
    ],
    example: "Pour un tableau de bord, nommez-le 'DashboardUI' et décrivez les widgets qu'il contient."
  }
};

const TemplateHelp = ({ type, onClose }) => {
  const helpData = templateHelp[type] || {
    title: "Aide sur les modèles",
    description: "Utilisez les modèles pour accélérer la création de vos nœuds.",
    steps: ["Sélectionnez un modèle qui correspond à votre besoin", "Personnalisez les propriétés", "Créez le nœud"],
    example: "Essayez différents modèles pour structurer votre graphe de connaissance."
  };
  
  return (
    <div className="template-help-modal bg-white p-4 rounded-lg shadow-lg border border-blue-200 max-w-md">
      <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
        <h3 className="font-bold text-blue-800 text-lg">{helpData.title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FaTimes />
        </button>
      </div>
      
      <p className="text-gray-700 mb-3">{helpData.description}</p>
      
      <div className="mb-3">
        <h4 className="font-semibold text-gray-800 mb-1">Étapes recommandées:</h4>
        <ol className="list-decimal pl-5 text-gray-600 text-sm space-y-1">
          {helpData.steps.map((step, idx) => (
            <li key={idx}>{step}</li>
          ))}
        </ol>
      </div>
      
      <div className="bg-blue-50 p-3 rounded">
        <h4 className="font-semibold text-blue-800 mb-1">Exemple:</h4>
        <p className="text-sm text-blue-700">{helpData.example}</p>
      </div>
    </div>
  );
};

export function TemplateHelpButton({ type, className }) {
  const [showHelp, setShowHelp] = useState(false);
  
  return (
    <>
      <button 
        onClick={(e) => {
          e.stopPropagation();
          setShowHelp(true);
        }}
        className={`text-gray-400 hover:text-gray-600 ${className}`}
        title="Aide sur ce modèle"
      >
        <FaQuestionCircle />
      </button>
      
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={() => setShowHelp(false)}>
          <div onClick={e => e.stopPropagation()}>
            <TemplateHelp type={type} onClose={() => setShowHelp(false)} />
          </div>
        </div>
      )}
    </>
  );
}

export default TemplateHelp;